// ---------------------------------------------------------------------------
// faqSections — the FAQ's section rail. Ports the `data-section-key` behaviour
// the original did in jQuery: clicking a title in the left rail marks it
// `.active` and reveals that section's questions in the right-hand pane (and,
// below `tablet-down`, in the copy inlined under the title itself).
//
// The port kept the markup and the CSS that hides every section, but never
// brought the selector across, so no section could ever be chosen and all 30
// questions on /pricing/faq — and the same set on /pricing/overview — were
// unreachable.
//
// Markup contract (macros/faq.html):
//   <div data-faq-sections>
//     <div class="accordion__title" data-section-key="k">…</div>   the rail
//     <… data-section-key="k">…</…>                                 revealed
//   </div>
//
// Rail titles take `.active`, which both pages already style. Everything else
// keyed to a section is shown or hidden with the `hidden` attribute rather than
// a class: the stylesheet sets no `display` on those elements, so the UA's
// `[hidden] { display: none }` wins with no specificity battle — which matters
// because the two pages reach the same elements through different ancestor
// chains (`.faq__sections-items …` at one specificity, `.pricingsubpage__faq
// .faq__sections .faq__sections-items …` at another).
// ---------------------------------------------------------------------------

const initGroup = (root: HTMLElement): void => {
	const titles = Array.from(
		root.querySelectorAll<HTMLElement>('.accordion__title[data-section-key]'),
	);
	if (!titles.length) return;

	// Everything keyed to a section that is not a rail title: the desktop
	// questions and the mobile per-section panels.
	const targets = Array.from(root.querySelectorAll<HTMLElement>('[data-section-key]')).filter(
		(el) => !el.classList.contains('accordion__title'),
	);
	if (!targets.length) return;

	const select = (key: string): void => {
		for (const title of titles) {
			const on = title.dataset.sectionKey === key;
			title.classList.toggle('active', on);
			title.setAttribute('aria-pressed', on ? 'true' : 'false');
		}
		for (const target of targets) {
			target.toggleAttribute('hidden', target.dataset.sectionKey !== key);
		}
	};

	for (const title of titles) {
		title.setAttribute('role', 'button');
		title.setAttribute('tabindex', '0');
		title.addEventListener('click', () => select(title.dataset.sectionKey ?? ''));
		title.addEventListener('keydown', (event) => {
			if (event.key !== 'Enter' && event.key !== ' ') return;
			event.preventDefault();
			select(title.dataset.sectionKey ?? '');
		});
	}

	// A link straight to one answer selects that answer's section, so a deep
	// link lands on visible content instead of on a hidden question. Otherwise
	// the first section — the data's own order, and what the live site opens on.
	const deepLinked = location.hash ? document.getElementById(location.hash.slice(1)) : null;
	const fromHash =
		deepLinked && root.contains(deepLinked)
			? deepLinked.closest<HTMLElement>('[data-section-key]')
			: null;
	select(fromHash?.dataset.sectionKey ?? titles[0]?.dataset.sectionKey ?? '');
};

export const initFaqSections = (): void => {
	for (const root of document.querySelectorAll<HTMLElement>('[data-faq-sections]')) initGroup(root);
};
